import { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { useParams } from 'react-router-dom';
import { DuplicateIcon } from '@heroicons/react/outline';

import { defaultTransitionProps } from '../../../lib/animation';
import Tooltip from '../../../components/Tooltip';
import TimezoneSelector from '../../../components/TimezoneSelector';
import LanguageSelector from '../../../components/LanguageSelector';
import CountrySelector from '../../../components/CountrySelector';
import { useReferralSources } from '../../../hooks/useUIConstants';

import DeactivatePatientModal from '../../../components/Modals/DeactivatePatientModal';
import { getTimeStamp } from '../../../lib/time';
import { calculateAge } from '../../../lib/dateUtils';
import { usePatientProfileContext } from '..';
import Panel from './Panel';
import Contacts from './Contacts';
import Button from '../../../components/Button';
import { SubmitErrorHandler, useForm } from 'react-hook-form';
import {
  MAX_NAME_LENGTH,
  fullNameRegisterOptions,
  phoneRegisterOptions,
  emailRegisterOptions,
} from '../../../lib/register-options';
import Spinner from '../../../svgs/Spinner';
import toast from 'react-hot-toast';
import ToastAlert from '../../../components/ToastAlert';
import { ApolloError } from '@apollo/client';
import {
  PatientLifecycleStatusV2,
  useProviderUpdatePatientProfileMutation,
  useProviderUpdatePatientEmailMutation,
  useGetMyMembershipsQuery,
  useGetActiveProviderContextQuery,
  MembershipStatus,
  useProgramNotesQuery,
} from '../../../../generated/graphql';
import { fullNameFromFirstAndLast } from '../../../lib/string';
import ResendInvitesModal from '../../../components/Modals/ResendInvitesModal';
import { downloadAllProgramNotes } from '../../../lib/pdf';
import { customToast } from '../../../components/ToastAlert/customToast';
import { getCurrentTimezone } from '../../../pages/Schedule/CalendarV2/utils/timezoneUtils';
import { validatePostalCode } from '../../../lib/addressValidation';
import { useAuth } from '../../../../contexts/AuthContext';

interface AccountFormData {
  phoneNumber: string;
  name: string;
  email?: string;
  // Demographic Information
  participantId?: string;
  dateOfBirth?: string;
  age?: number;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  preferredLanguage?: string;
  timezone?: string;
  // Program Information
  programStartDate?: string;
  programEndDate?: string;
  referralSource?: string;
  referralSourceOther?: string;
}

const Account = () => {
  const params = useParams();
  const programId = params.programId || '';

  const { patient, refetchPatient, isMobileActiveProfile } =
    usePatientProfileContext();
  const { authedProviderUser } = useAuth();

  // New focused mutations
  const [updateProfileMutation] = useProviderUpdatePatientProfileMutation();
  const [updateEmailMutation] = useProviderUpdatePatientEmailMutation();

  const [isDeactivatePatientModalOpen, setIsDeactivatePatientModalOpen] =
    useState(false);
  const [isResendInvitesModalOpen, setIsResendInvitesModalOpen] =
    useState(false);
  const [isExportingPrivateNotes, setIsExportingPrivateNotes] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Fetch program notes for export
  const { data: programNotesData } = useProgramNotesQuery({
    variables: { programId },
    skip: !programId,
  });

  // Get current user's memberships to check for external collaborator status
  const { data: membershipsData } = useGetMyMembershipsQuery({
    variables: { input: { statuses: [MembershipStatus.Active] } },
  });

  // Get current active provider context
  const { data: activeProviderData } = useGetActiveProviderContextQuery();

  // Check if user is an external collaborator for the CURRENT active provider context
  const currentProviderId = activeProviderData?.activeProviderContext?.id;
  const isExternalCollaborator = currentProviderId
    ? membershipsData?.myMemberships?.some(
        (membership) =>
          membership.provider.id === currentProviderId &&
          membership.isExternalCollaborator,
      )
    : false;

  const canEditEmail = !isExternalCollaborator;

  // Copy to clipboard function
  const handleCopyParticipantId = async () => {
    if (!patient?.participantId) return;

    try {
      await navigator.clipboard.writeText(patient.participantId);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Participant ID copied to clipboard"
        />
      ));
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="error"
          message="Failed to copy Participant ID"
        />
      ));
    }
  };

  const handleCopyPatientId = async () => {
    if (!patient?.id) return;

    try {
      await navigator.clipboard.writeText(patient.id);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Patient ID copied to clipboard"
        />
      ));
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="error"
          message="Failed to copy Patient ID"
        />
      ));
    }
  };

  // Field-level validation handled via react-hook-form register options

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<AccountFormData>({
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: patient?.phoneNumber || '',
      name: fullNameFromFirstAndLast(patient?.firstName, patient?.lastName),
      email: patient?.email || '',
      // Demographic Information - Now using real backend data
      participantId: patient?.participantId || '',
      dateOfBirth: patient?.dateOfBirth
        ? new Date(patient.dateOfBirth).toISOString().split('T')[0]
        : '',
      age:
        patient?.age !== null && patient?.age !== undefined
          ? patient.age
          : undefined,
      addressStreet: patient?.addressStreet || '',
      addressCity: patient?.addressCity || '',
      addressState: patient?.addressState || '',
      addressPostalCode: patient?.addressPostalCode || '',
      addressCountry: patient?.addressCountry || '',
      preferredLanguage: patient?.preferredLanguage || '',
      timezone: patient?.timezone || getCurrentTimezone(),
      // Program Information
      programStartDate: patient?.programStartDate || '',
      programEndDate: patient?.programEndDate || '',
      referralSource: patient?.referralSource || 'Healthcare Provider',
    },
  });

  // Reinitialize form when patient or active provider changes to avoid stale/empty values
  useEffect(() => {
    reset({
      phoneNumber: patient?.phoneNumber || '',
      name: fullNameFromFirstAndLast(patient?.firstName, patient?.lastName),
      email: patient?.email || '',
      participantId: patient?.participantId || '',
      dateOfBirth: patient?.dateOfBirth
        ? new Date(patient.dateOfBirth).toISOString().split('T')[0]
        : '',
      age:
        patient?.age !== null && patient?.age !== undefined
          ? patient.age
          : undefined,
      addressStreet: patient?.addressStreet || '',
      addressCity: patient?.addressCity || '',
      addressState: patient?.addressState || '',
      addressPostalCode: patient?.addressPostalCode || '',
      addressCountry: patient?.addressCountry || '',
      preferredLanguage: patient?.preferredLanguage || '',
      timezone: patient?.timezone || getCurrentTimezone(),
      programStartDate: patient?.programStartDate || '',
      programEndDate: patient?.programEndDate || '',
      referralSource: patient?.referralSource || 'Healthcare Provider',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProviderId,
    patient?.id,
    patient?.preferredLanguage,
    patient?.addressCountry,
    patient?.addressStreet,
    patient?.addressCity,
    patient?.addressState,
    patient?.addressPostalCode,
    patient?.timezone,
    patient?.dateOfBirth,
    patient?.age,
    patient?.participantId,
    patient?.programStartDate,
    patient?.programEndDate,
    patient?.referralSource,
  ]);

  const handleErrors: SubmitErrorHandler<AccountFormData> = (errors) => {
    console.log('errors:', errors);
  };

  const contactData = [
    {
      label: 'Email',
      ...(canEditEmail
        ? {
            register: register('email', emailRegisterOptions),
            errorMessage: errors.email?.message,
            disabled: false,
          }
        : {
            value: patient?.email,
            disabled: true,
            readOnly: true,
          }),
    },
    {
      label: 'Name',
      register: register('name', fullNameRegisterOptions),
      errorMessage: errors.name?.message,
      maxLength: MAX_NAME_LENGTH,
    },
    {
      label: 'Phone',
      placeholder: '(817) 569-8900',
      disabled: false,
      register: register('phoneNumber', phoneRegisterOptions),
      errorMessage: errors.phoneNumber?.message,
    },
  ];

  // New Address section
  const addressData = [
    {
      label: 'Street Address',
      register: register('addressStreet'),
      errorMessage: errors.addressStreet?.message,
      placeholder: '123 Main Street',
    },
    {
      label: 'City',
      register: register('addressCity'),
      errorMessage: errors.addressCity?.message,
      placeholder: 'New York',
    },
    {
      label: 'State/Province',
      register: register('addressState'),
      errorMessage: errors.addressState?.message,
      placeholder: 'NY',
    },
    {
      label: 'Postal Code',
      register: register('addressPostalCode', {
        validate: validatePostalCode,
      }),
      errorMessage: errors.addressPostalCode?.message,
      placeholder: '10001',
    },
    {
      label: 'Country',
      customComponent: (
        <div className="mb-1 flex w-full flex-row items-center justify-between">
          <div className="text-caption text-neutral-125">Country</div>
          <div className="flex w-3/5 min-w-[300px] flex-col mdlg:min-w-[200px]">
            <CountrySelector
              value={watch('addressCountry') || ''}
              onChange={(value) =>
                setValue('addressCountry', value, {
                  shouldDirty: true,
                })
              }
              errorMessage={errors.addressCountry?.message}
            />
          </div>
        </div>
      ),
    },
  ];

  const accountData = isMobileActiveProfile
    ? [
        {
          label: 'Mobile app activation date',
          value: getTimeStamp(patient?.startTime, false),
          disabled: true,
          readOnly: true,
        },
      ]
    : [
        {
          label: 'Last mobile app invitation date',
          value: patient?.onboardingLastProgramInviteSentAt
            ? getTimeStamp(patient?.onboardingLastProgramInviteSentAt, false)
            : 'Not invited yet',
          disabled: true,
          readOnly: true,
        },
      ];

  // Watch form values for conditional rendering and age calculation
  const watchedReferralSource = watch('referralSource');
  const watchedDateOfBirth = watch('dateOfBirth');
  const watchedProgramStartDate = watch('programStartDate');

  // Auto-calculate age when date of birth changes
  useEffect(() => {
    if (watchedDateOfBirth) {
      const calculatedAge = calculateAge(watchedDateOfBirth);
      if (calculatedAge !== null) {
        // Always update age when date of birth is provided since field is read-only
        setValue('age', calculatedAge);
      }
    }
    // Note: We don't clear age when DOB is removed to preserve manually entered age values
  }, [watchedDateOfBirth, setValue]);

  // Custom Participant ID field component
  const ParticipantIdField = () => {
    const participantId = patient?.participantId || '';
    const hasId = Boolean(participantId);

    return (
      <div className="mb-2 flex w-full flex-row items-center justify-between">
        <div className="text-caption text-neutral-125">Participant ID</div>
        <div className="flex w-3/5 min-w-[300px] flex-col mdlg:min-w-[200px]">
          <div className="relative">
            <div
              className="flex items-center gap-2 rounded border-2 border-transparent bg-transparent py-3 px-4 text-body text-neutral-125 text-green-150"
              onClick={hasId ? handleCopyParticipantId : undefined}
            >
              <span className="font-mono text-left">
                {participantId || 'No ID assigned'}
              </span>
              {hasId && (
                <Tooltip
                  content="Click to copy"
                  placement="top"
                  className="w-auto min-w-0 px-2 py-1"
                >
                  <DuplicateIcon className="h-4 w-4 cursor-pointer text-neutral-200 transition-colors hover:text-green-150" />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const demographicData = [
    {
      label: 'Participant ID',
      customComponent: <ParticipantIdField />,
    },
    {
      label: 'Date of Birth',
      type: 'date' as const,
      register: register('dateOfBirth', {
        validate: (value) => {
          if (!value) return true;
          const date = new Date(value);
          const now = new Date();
          const age = now.getFullYear() - date.getFullYear();
          return (
            (date < now && age >= 0 && age <= 120) ||
            'Date of birth must be in the past and reasonable'
          );
        },
      }),
      errorMessage: errors.dateOfBirth?.message,
    },
    {
      label: 'Age',
      type: 'number' as const,
      register: register('age', {
        setValueAs: (originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }
          const parsed = Number(originalValue);
          return Number.isNaN(parsed) ? originalValue : parsed;
        },
        validate: (value) => {
          if (value === undefined) return true;
          if (!Number.isInteger(value)) return 'Age must be a whole number';
          if (value < 0) return 'Age must be 0 or greater';
          if (value > 200) return 'Age must be 200 or less';
          return true;
        },
      }),
      errorMessage: errors.age?.message,
      placeholder: watchedDateOfBirth
        ? 'Auto-calculated from date of birth'
        : 'Enter age manually',
      min: 0,
      max: 120,
      disabled: !!watchedDateOfBirth,
      readOnly: !!watchedDateOfBirth,
    },
    {
      label: 'Preferred Language',
      customComponent: (
        <div className="mb-1 flex w-full flex-row items-center justify-between">
          <div className="text-caption text-neutral-125">
            Preferred Language
          </div>
          <div className="flex w-3/5 min-w-[300px] flex-col mdlg:min-w-[200px]">
            <LanguageSelector
              value={watch('preferredLanguage') || ''}
              onChange={(value) =>
                setValue('preferredLanguage', value, {
                  shouldDirty: true,
                })
              }
            />
          </div>
        </div>
      ),
    },
    // Move Timezone here as the last item in Personal Information
    {
      label: 'Timezone',
      customComponent: (
        <div className="mb-2 flex w-full flex-row items-center justify-between">
          <div className="text-caption text-neutral-125">Timezone</div>
          <div className="flex w-3/5 min-w-[300px] flex-col mdlg:min-w-[200px]">
            <TimezoneSelector
              value={watch('timezone') || 'America/New_York'}
              onChange={(value) =>
                setValue('timezone', value, { shouldDirty: true })
              }
            />
          </div>
        </div>
      ),
    },
  ];

  // Get referral sources for the dropdown
  const { referralSources } = useReferralSources();

  const programData = [
    {
      label: 'Program Start Date',
      type: 'date' as const,
      register: register('programStartDate', {
        validate: (value) => {
          if (!value) return true;
          const date = new Date(value);
          return !isNaN(date.getTime()) || 'Program start date must be valid';
        },
      }),
      errorMessage: errors.programStartDate?.message,
    },
    {
      label: 'Program End Date',
      type: 'date' as const,
      register: register('programEndDate', {
        validate: (value) => {
          if (!value) return true;
          const endDate = new Date(value);
          if (isNaN(endDate.getTime()))
            return 'Program end date must be valid and after or equal to start date';
          const startVal = watchedProgramStartDate;
          if (startVal) {
            const startDate = new Date(startVal);
            if (!isNaN(startDate.getTime()) && endDate < startDate) {
              return 'Program end date must be after or equal to start date';
            }
          }
          return true;
        },
      }),
      errorMessage: errors.programEndDate?.message,
    },
    {
      label: 'Referral Source',
      type: 'select' as const,
      options: referralSources || [],
      register: register('referralSource'),
      errorMessage: errors.referralSource?.message,
    },
    ...(watchedReferralSource === 'Other'
      ? [
          {
            label: 'Other Referral Source',
            register: register('referralSourceOther', {
              required: 'Please specify the referral source',
            }),
            errorMessage: errors.referralSourceOther?.message,
            placeholder: 'Please specify',
          },
        ]
      : []),
  ];

  const onSubmit = async (formData: AccountFormData) => {
    try {
      setSubmitting(true);

      if (!patient?.programInstanceId) {
        throw new Error('Patient program ID is required');
      }

      // Check if email changed and user has permission
      const emailChanged =
        canEditEmail &&
        formData.email &&
        formData.email.trim() !== patient?.email;

      // Update email FIRST (if changed) - separate mutation for security
      if (emailChanged && formData.email) {
        await updateEmailMutation({
          variables: {
            input: {
              programId: patient.programInstanceId,
              email: formData.email.trim(),
            },
          },
        });
      }

      // Update profile and program fields - separate mutation
      const profileResult = await updateProfileMutation({
        variables: {
          input: {
            programId: patient.programInstanceId,
            name: formData.name.trim(),
            phoneNumber: formData.phoneNumber || null,
            // Demographic fields
            participantId:
              (formData.participantId ?? '').trim() === ''
                ? ''
                : formData.participantId ?? null,
            dateOfBirth: formData.dateOfBirth || null,
            age: formData.age || null,
            addressStreet: formData.addressStreet || null,
            addressCity: formData.addressCity || null,
            addressState: formData.addressState || null,
            addressPostalCode: formData.addressPostalCode || null,
            addressCountry: formData.addressCountry || null,
            preferredLanguage: formData.preferredLanguage || null,
            timezone: formData.timezone || null,
            // Program fields
            programStartDate: formData.programStartDate || null,
            programEndDate: formData.programEndDate || null,
            referralSource:
              formData.referralSource === 'Other'
                ? formData.referralSourceOther
                : formData.referralSource || null,
          },
        },
      });

      // If backend auto-generated participantId, refresh the value
      const updated = profileResult.data?.providerUpdatePatientProfile;
      if (updated?.participantId && !formData.participantId) {
        setValue('participantId', updated.participantId);
      }

      await refetchPatient?.();

      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Successfully saved your changes"
        />
      ));
    } catch (err: unknown) {
      // Extract error message from GraphQL response
      const errorMessage =
        (err as ApolloError)?.graphQLErrors?.[0]?.message ||
        (err instanceof Error ? err.message : 'Failed to save changes.');

      toast.custom(({ visible }) => (
        <ToastAlert isVisible={visible} level="error" message={errorMessage} />
      ));
      console.error(err as Error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportPrivateNotes = async () => {
    try {
      setIsExportingPrivateNotes(true);

      const programNotes = programNotesData?.programNotes || [];

      if (programNotes.length === 0) {
        customToast.error('No private notes to export');
        return;
      }

      const patientName = patient?.name || patient?.firstName || 'Client';
      const wordmarkLogoUrl =
        patient?.providerWordmarkLogo?.url ||
        authedProviderUser?.wordmarkLogoMedia?.url ||
        null;
      const downloadCount = await downloadAllProgramNotes(programNotes, {
        patientName,
        wordmarkLogoUrl,
        participantId: patient?.participantId || null,
      });

      customToast.success(
        `Successfully exported ${downloadCount} private note${
          downloadCount !== 1 ? 's' : ''
        }`,
      );
    } catch (err) {
      customToast.error('Failed to export private notes');
      console.error(err as Error);
    } finally {
      setIsExportingPrivateNotes(false);
    }
  };

  return (
    <>
      <Transition
        show={Boolean(patient)}
        {...defaultTransitionProps}
        className="w-full"
      >
        <div className="grid w-full max-w-[1200px] grid-cols-1 gap-x-8 pb-10 mdlg:grid-cols-2">
          <div className="sticky top-0 z-10 col-span-1 mb-4 flex w-full items-center bg-white py-3 shadow-sm mdlg:col-span-2">
            <Button
              title="Save changes"
              size="medium"
              onClick={handleSubmit(onSubmit, handleErrors)}
              disabled={submitting || !isDirty}
              {...(submitting && {
                IconComponent: Spinner,
              })}
            />
          </div>
          <div className="flex flex-col">
            <Panel title="Contact information" data={contactData} />
            <Panel title="Address" data={addressData} />
            <Panel title="Personal Information" data={demographicData} />
          </div>
          <div className="flex flex-col">
            <Contacts />
            <Panel title="Program Information" data={programData} />
            <Panel
              title="Mobile app status"
              data={accountData}
              {...(!isMobileActiveProfile && {
                onMobileInviteButtonClick: () => {
                  setIsResendInvitesModalOpen(true);
                },
              })}
            />
            {patient?.id && (
              <Panel
                title="API information"
                data={[
                  {
                    label: 'Client ID',
                    customComponent: (
                      <div className="api-information mb-2 flex w-full flex-row items-center justify-between">
                        <div className="text-caption text-neutral-125">
                          Client ID
                        </div>
                        <div className="flex w-3/5 min-w-[300px] flex-col mdlg:min-w-[200px]">
                          <div className="relative">
                            <div
                              className="client-id flex items-center gap-2 rounded border-2 border-transparent bg-transparent py-3 px-4 text-caption text-green-150"
                              onClick={handleCopyPatientId}
                            >
                              <span className="font-mono text-left">
                                {patient.id}
                              </span>
                              <Tooltip
                                content="Click to copy"
                                placement="top"
                                className="w-auto min-w-0 px-2 py-1"
                              >
                                <DuplicateIcon className="h-4 w-4 cursor-pointer text-neutral-200 transition-colors hover:text-green-150" />
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            )}
            <Panel
              title="Settings"
              hasArchiveClient
              setIsDeactivatePatientModalOpen={setIsDeactivatePatientModalOpen}
              hasExportPrivateNotes
              onExportPrivateNotesClick={handleExportPrivateNotes}
              isExportingPrivateNotes={isExportingPrivateNotes}
              hasNoPrivateNotes={
                !programNotesData?.programNotes ||
                programNotesData.programNotes.length === 0
              }
            />
          </div>
        </div>
      </Transition>
      <DeactivatePatientModal
        isSinglePatient
        singlePatient={patient}
        isModalOpen={isDeactivatePatientModalOpen}
        patientsObject={null}
        setClosed={() => setIsDeactivatePatientModalOpen(false)}
      />
      {!isMobileActiveProfile && (
        <ResendInvitesModal
          isSinglePatient
          isModalOpen={isResendInvitesModalOpen}
          singlePatient={patient}
          patientLifecycleStatus={PatientLifecycleStatusV2.Invited}
          setClosed={() => setIsResendInvitesModalOpen(false)}
          refetchPatient={refetchPatient}
        />
      )}
    </>
  );
};

export default Account;
